import Button, { ButtonProps, ButtonStyle } from 'components/common/Button';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BJProfile } from 'types/bjProfile';
import { ProfileData } from '../Profile';

type TypeButtonsProps = {
  userInfo: UserInfo;
  data: ProfileData;
  bjProfile?: BJProfile | null;
};

const TypeButtons = ({ userInfo, data, bjProfile = null }: TypeButtonsProps) => {
  const { close } = useBottomSheet();
  const [buttons, setButtons] = useState<ButtonProps[]>([]);
  const { type, category, isOnline, canCall } = data;

  // TapToMatch 상태를 컴포넌트 레벨에서 관리
  const tappedUser = useMemo(() => {
    if (!userInfo?.fbUid) return [];

    return TapToMatchDataManager.getMyList().find((match: any) => match.otherId === userInfo.fbUid);
  }, [userInfo?.fbUid]);

  const paused = TapToMatchWaitingListModal.getSwitchState();

  const getTapToMatchButtonStyle = useCallback((): ButtonStyle => {
    const baseStyle: ButtonStyle = {
      borderRadius: 'lg',
      color: isOnline ? 'btn-bg-secondary' : 'btn-bg-alert',
      fontColor: isOnline ? 'btn-txt-secondary' : 'white',
      shadow: 'shadow-03',
    };

    if (isOnline) {
      return {
        ...baseStyle,
        text: tappedUser ? brsc.taptomatch_delete_btn : brsc.taptomatch_tap,
        icon: tappedUser ? { name: `icon/ic_${paused ? 'pause' : 'check'}.svg`, size: 28, color: 'filter-base-ic-purple' } : undefined,
        trailing: !paused ? <TapToMatchCountDownText time={tappedUser?.at?.valueOf()} /> : undefined,
      };
    }

    return {
      ...baseStyle,
      text: '',
      icon: { name: 'icon/ic_disconnect_white.svg', size: 28, color: 'filter-base-ic-white' },
      trailing: undefined,
    };
  }, [isOnline, tappedUser, paused]);

  const BUTTON_CONFIGS = useMemo<Record<string, ButtonProps>>(
    () => ({
      message: {
        onClick: () => {
          const { nick, gender, country } = userInfo;
          CommonUI.tryChat(userInfo.fbUid, nick, gender, country, () => {}, false, true, false, category, {
            page: Transition.getCurrentPage() || Transition.getCurrentTab(),
            btn: 'extrainfo',
            prevLoc: Transition.getPrevHistoryName(),
          });
        },
        style: {
          text: 'Message',
          fontColor: 'btn-txt-secondary',
          color: 'btn-bg-secondary',
          borderRadius: 'lg',
          icon: { name: 'tutorial/ic_msg_black.svg', size: 32, color: 'filter-base-ic-purple' },
          shadow: 'shadow-03',
        },
      },
      call: {
        onClick: () => {
          CommonUI.makeCall(userInfo, () => {}, 'profile', { fromWhere: 'profile' });
        },
        style: {
          text: 'Call',
          fontColor: 'btn-txt-secondary',
          color: 'btn-bg-secondary',
          borderRadius: 'lg',
          icon: { name: 'tutorial/ic_video_purple.svg', size: 32, color: 'filter-base-ic-purple' },
          shadow: 'shadow-03',
        },
      },
      settings: {
        onClick: () => Transition.showPage(Def.PAGE_MYPAGE),
        style: {
          text: '',
          color: 'btn-bg-normal',
          borderRadius: 'lg',
          icon: { name: 'profile/setting_white.svg', size: 32 },
          shadow: 'shadow-03',
        },
      },
      editNick: {
        onClick: () => Transition.showPage(Def.PAGE_EDITPICNICK),
        style: {
          text: '',
          color: 'btn-bg-normal',
          borderRadius: 'lg',
          icon: { name: 'profile/my_edit.svg', size: 20 },
          shadow: 'shadow-03',
        },
      },
      tapToMatch: {
        onClick: () => {
          if (!isOnline) {
            CommonUI.showToast(document.getElementById('hMainContainer'), { text: brsc.taptomatch_toast_3, type: 'warn', position: 'top' });
            return;
          }

          close('Profile', { skipConfirm: false, data: true });
        },
        style: getTapToMatchButtonStyle(),
      },
      friendAccept: {
        onClick: () => {
          PageChatRoom.acceptFriendRequest();
        },
        style: {
          icon: { name: `icon/ic_check.svg`, size: 24, color: 'filter-btn-ic-primary' },
          text: brsc.accept_btn,
          color: 'btn-bg-primary',
          fontColor: 'btn-txt-primary',
          borderRadius: 'lg',
          shadow: 'shadow-03',
        },
        className: 'gap-[var(--spacing-sm)]',
      },
      goToHome: {
        onClick: () => {
          close('Profile');
          Transition.moveTab(Def.TAB_HOME);
        },
        style: {
          text: brsc.goto_home,
          color: 'btn-bg-secondary',
          fontColor: 'btn-txt-secondary',
          borderRadius: 'lg',
          shadow: 'shadow-03',
        },
      },
      primeCall: {
        onClick: () => {
          close('Profile', { data: { action: 'call', cost: bjProfile?.cost ?? 0 } });
        },
        style: {
          icon: { name: bassets.bermuda.icon_set.video.svg, size: 32, color: 'filter-base-ic-white' },
          text: brsc.call_icon,
          background: 'var(--bcs-semantic-base-bg-prime)',
          fontColor: 'white',
          borderRadius: 'lg',
          shadow: 'shadow-03',
        },
        className: 'gap-[var(--spacing-sm)]',
      },
    }),
    [userInfo, close, getTapToMatchButtonStyle, category, isOnline, bjProfile]
  );

  const getButtonsForType = useCallback(() => {
    if (!userInfo) return [];

    const isOwnProfile = myinfo.fbUid === userInfo.fbUid;

    if (isOwnProfile) {
      return [BUTTON_CONFIGS.settings, BUTTON_CONFIGS.editNick];
    }

    if (userInfo.fbUid === PageLogin.fbIdBermuda) {
      return [BUTTON_CONFIGS.goToHome];
    }

    switch (type) {
      case 'message':
        return [BUTTON_CONFIGS.message];
      case 'call':
        return [BUTTON_CONFIGS.call];
      case 'friend':
        return [
          {
            ...BUTTON_CONFIGS.message,
            style: {
              ...BUTTON_CONFIGS.message.style,
              text: '',
              color: 'btn-bg-normal' as const,
              icon: { name: 'tutorial/ic_msg_black.svg', size: 32 },
            },
          },
          { ...BUTTON_CONFIGS.call, style: { ...BUTTON_CONFIGS.call.style, text: '' } },
        ];
      case 'tapToMatch':
        return [BUTTON_CONFIGS.tapToMatch];
      case 'friendAccept':
        return [BUTTON_CONFIGS.friendAccept];
      case 'prime':
        return canCall ? [BUTTON_CONFIGS.primeCall] : [];
      default:
        return [];
    }
  }, [BUTTON_CONFIGS, userInfo, type, canCall]);

  useEffect(() => {
    setButtons(getButtonsForType());
  }, [getButtonsForType]);

  return (
    <div className="sticky bottom-0 left-4 right-4 flex gap-2">
      {buttons.map((buttonProps: ButtonProps, index) => (
        <Button key={index} {...buttonProps} />
      ))}
    </div>
  );
};

const TapToMatchCountDownText = ({ time }: { time?: number }) => {
  const [millis, setMillis] = useState(0);

  useEffect(() => {
    if (time) {
      const limit = time + Def.TAP_TO_MATCH_REMAINING_TIME;
      const remainingTime = limit - Date.now();
      setMillis(remainingTime);
    }

    const interval = setInterval(() => {
      setMillis((prevMillis) => prevMillis - 1000);
    }, 1000);

    return () => clearInterval(interval);
  }, [time]);

  if (millis <= 0) {
    return <></>;
  }

  return <span className="mx-1 typography-headline-lg">{TimeUtil.getDateTimemmss(millis)}</span>;
};

export default TypeButtons;
